(() => {
  const LIMIT_TAG = "limit_quantity";
  const LIMIT_PROPERTY = "_limit_quantity";
  const MAX_PER_PRODUCT = 3;
  const cartEndpoint = `${window.Shopify.routes.root}cart.js`;
  const updateEndpoint = `${window.Shopify.routes.root}cart/update.js`;
  const productTagCache = new Map();

  let running = false;
  let queued = false;

  const readLimitProperty = (value) => {
    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      return value.toLowerCase() === "true" || value === "1";
    }

    return false;
  };

  const fetchCart = async () => {
    const response = await fetch(cartEndpoint, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error(`Unable to fetch cart: ${response.status}`);
    }

    return response.json();
  };

  const updateCart = async (updates) => {
    const response = await fetch(updateEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`Unable to update cart: ${response.status}`);
    }

    return response.json();
  };

  const hasLimitTag = async (lineItem) => {
    const lineProps = lineItem?.properties || {};

    if (readLimitProperty(lineProps[LIMIT_PROPERTY])) {
      return true;
    }

    const handle = lineItem?.handle;
    if (!handle) {
      return false;
    }

    if (productTagCache.has(handle)) {
      return productTagCache.get(handle);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`, {
        credentials: "same-origin",
      });

      if (!response.ok) {
        productTagCache.set(handle, false);
        return false;
      }

      const product = await response.json();
      const tags = Array.isArray(product?.tags) ? product.tags : [];
      const isLimited = tags.some((tag) => String(tag).toLowerCase() === LIMIT_TAG);
      productTagCache.set(handle, isLimited);
      return isLimited;
    } catch (error) {
      console.warn("Unable to resolve product tags for quantity limit", error);
      productTagCache.set(handle, false);
      return false;
    }
  };

  const computeUpdates = async (cart) => {
    const updates = {};
    const quantityByProduct = new Map();

    for (const lineItem of cart.items || []) {
      const limited = await hasLimitTag(lineItem);
      if (!limited) {
        continue;
      }

      const productId = lineItem.product_id || lineItem.id;
      const alreadyAllocated = quantityByProduct.get(productId) || 0;
      const remaining = Math.max(MAX_PER_PRODUCT - alreadyAllocated, 0);
      const allowedQty = Math.min(lineItem.quantity, remaining);

      quantityByProduct.set(productId, alreadyAllocated + allowedQty);

      if (allowedQty !== lineItem.quantity) {
        updates[lineItem.key] = allowedQty;
      }
    }

    return updates;
  };

  const enforceLimit = async (reason = "unknown") => {
    if (running) {
      queued = true;
      return;
    }

    running = true;

    try {
      const cart = await fetchCart();
      const updates = await computeUpdates(cart);
      const hasUpdates = Object.keys(updates).length > 0;

      if (hasUpdates) {
        const updatedCart = await updateCart(updates);
        document.dispatchEvent(
          new CustomEvent("limitQuantity:enforced", {
            detail: { reason, updates, cart: updatedCart },
          })
        );
      }
    } catch (error) {
      console.warn("Quantity limit enforcement failed", error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        enforceLimit("queued");
      }
    }
  };

  document.addEventListener("afterCartChange", () => {
    enforceLimit("afterCartChange");
  });

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        if (document.body.classList.contains("template-cart")) {
          enforceLimit("cartLoad");
        }
      },
      { once: true }
    );
  } else if (document.body.classList.contains("template-cart")) {
    enforceLimit("cartLoad");
  }
})();
